import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { QueryClient, QueryObserver } from '@tanstack/react-query'
import { useAppAuth } from '@/context/AppAuthContext'
import { getCurrentInstanceConfiguration } from '@/services/instanceConfiguration'
import { DEFAULT_BRANDING_STATE, normalizeAppProfileState, type BrandingState } from './brandingState'

export type BrandingLoadState = 'loading' | 'ready' | 'error'

export type BrandingLoadResult = {
  value: BrandingState
  loadState: BrandingLoadState
  error: string
  reload: () => void
}

const LOAD_STATES: Record<string, BrandingLoadState> = {
  pending: 'loading', success: 'ready', error: 'error',
}

/** One accepted configuration per login. Token refresh must not replace the active Host. */
export function useBrandingLoad(): BrandingLoadResult {
  const { hasAccessToken, authStage } = useAppAuth()
  const enabled = hasAccessToken && authStage === 'idle'
  const binding = useMemo(() => {
    const client = new QueryClient()
    const queryKey = ['instance-configuration', enabled]
    const queryFn = async ({ signal }: { signal: AbortSignal }) => {
      const configuration = await getCurrentInstanceConfiguration(signal)
      signal.throwIfAborted()
      const profile = normalizeAppProfileState({ ...configuration.app_profile,
        userId: configuration.user_id, instanceId: configuration.instance_id,
        domainPackDomain: configuration.domain_pack_domain, domainPackVersion: configuration.domain_pack_version })
      if (!profile) throw new TypeError('当前实例配置不可用')
      return profile
    }
    const observer = new QueryObserver<BrandingState, Error>(client, {
      queryKey, queryFn, enabled: false, retry: false, staleTime: Infinity,
    })
    const read = () => client.fetchQuery({ queryKey, queryFn, retry: false, staleTime: Infinity })
    return { client, observer, queryKey, read }
  }, [enabled])
  const subscribe = useCallback((notify: () => void) => binding.observer.subscribe(notify), [binding])
  const getSnapshot = useCallback(() => binding.observer.getCurrentResult(), [binding])
  const result = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  useEffect(() => {
    if (!enabled) return
    void binding.read().catch(() => {})
    return () => { void binding.client.cancelQueries({ queryKey: binding.queryKey }) }
  }, [binding, enabled])

  const reload = useCallback(() => {
    // Retry only unresolved reads; accepted config remains pinned to this login/page.
    if (!enabled || binding.client.getQueryData(binding.queryKey)) return
    void binding.read().catch(() => {})
  }, [binding, enabled])

  return useMemo(() => ({
    value: result.data ?? DEFAULT_BRANDING_STATE,
    loadState: enabled ? LOAD_STATES[result.status] : 'loading',
    error: result.error?.message ?? '',
    reload,
  }), [enabled, reload, result])
}

/** Retain the presentation hook; page entries gate Host mounting on configuration readiness. */
export function useBranding(): BrandingState {
  return useBrandingLoad().value
}
